
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  Truck, MapPin, X, ChevronRight, LogOut, DollarSign, 
  User, Info, Camera, Bell, ShieldCheck, 
  CheckCircle2, Siren, Wrench, Loader2, Send, 
  Receipt, PackagePlus, ArrowLeft, FileBadge, Check, 
  UploadCloud, 
  RefreshCw, ShieldAlert, AlertTriangle, Building2,
  KeyRound, Lock, MessageSquare
} from 'lucide-react';
import { Order, OrderStatus, AppNotification, Company } from './types';
import { Badge, ToastNotification, Card } from './SharedComponents';

export const DriverPage = ({ orders, user, companies = [], onLogout, onSosAlert, onRequestExtra, onRequestRefund, onUpdateUser, driverPayments: initialPayments = [], notifications = [], onMarkAllRead, onUpdateOrder }: any) => {
  const [activeTab, setActiveTab] = useState<'route' | 'finance' | 'profile'>('route');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [activeToast, setActiveToast] = useState<AppNotification | null>(null);
  const [localPayments, setLocalPayments] = useState(initialPayments);
  
  // Estados para Modais de Solicitação Financeira
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestType, setRequestType] = useState<'REFUND' | 'EXTRA' | null>(null);
  const [reqAmount, setReqAmount] = useState('');
  const [reqDesc, setReqDesc] = useState('');
  const [reqPhoto, setReqPhoto] = useState<string | null>(null);
  const [reqOrderId, setReqOrderId] = useState('');

  // Estados para Modais de Segurança e Senha
  const [showSosModal, setShowSosModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [sosType, setSosType] = useState<string | null>(null);
  const [sosDescription, setSosDescription] = useState('');
  const [passwordData, setPasswordData] = useState({ new: '', confirm: '' });

  const profilePhotoRef = useRef<HTMLInputElement>(null);
  const cnhPhotoRef = useRef<HTMLInputElement>(null);
  const reqPhotoRef = useRef<HTMLInputElement>(null);
  
  const unreadCount = notifications.filter((n: any) => !n.read).length;

  const myCompany = useMemo(() => {
    return companies.find((c: Company) => c.id === user.companyId) || { name: 'Unidade Sorocaba' };
  }, [companies, user.companyId]);

  useEffect(() => { setLocalPayments(initialPayments); }, [initialPayments]);

  const filteredTransactions = useMemo(() => {
    return [...localPayments]
      .filter((p: any) => String(p.userId) === String(user.id))
      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [localPayments, user.id]);

  const totalBalance = useMemo(() => filteredTransactions.reduce((acc: number, p: any) => acc + (Number(p.amount) || 0), 0), [filteredTransactions]);

  // HANDLERS
  const handleUpdateProfilePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => onUpdateUser({ ...user, avatar: reader.result as string });
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateCnh = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpdateUser({ ...user, cnhPhoto: reader.result as string });
        alert("Documento enviado para validação da central!");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCaptureRequestPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setReqPhoto(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSendFinancialRequest = async () => {
    if (!reqAmount || !reqDesc || !reqOrderId) return alert("Preencha todos os campos obrigatórios.");
    setIsSubmittingAction(true);
    try {
      if (requestType === 'REFUND') {
        await onRequestRefund(user.id, reqOrderId, parseFloat(reqAmount), reqDesc, reqPhoto);
      } else {
        await onRequestExtra(user.id, reqOrderId, parseFloat(reqAmount), reqDesc, reqPhoto);
      }
      alert("Solicitação enviada para auditoria da controladoria!");
      setShowRequestModal(false);
      resetRequestForm();
    } finally { setIsSubmittingAction(false); }
  };

  const resetRequestForm = () => {
    setReqAmount('');
    setReqDesc('');
    setReqPhoto(null);
    setReqOrderId('');
  };

  const handleExecuteSos = async () => {
    if (!sosType) return;
    if (sosType === 'OUTROS' && !sosDescription.trim()) return alert("Por favor, descreva a emergência.");
    
    setIsSubmittingAction(true);
    try {
      const finalMessage = sosType === 'OUTROS' ? `OUTROS: ${sosDescription}` : sosType;
      await onSosAlert(user.name, finalMessage);
      setShowSosModal(false);
      setSosType(null);
      setSosDescription('');
      alert("ALERTA ENVIADO! A central recebeu sua localização e prioridade.");
    } finally { setIsSubmittingAction(false); }
  };

  const handleResetPassword = () => {
    if (!passwordData.new || passwordData.new !== passwordData.confirm) {
        return alert("As senhas não coincidem ou estão vazias.");
    }
    setIsSubmittingAction(true);
    setTimeout(() => {
        alert("Senha redefinida com sucesso!");
        setShowResetPasswordModal(false);
        setPasswordData({ new: '', confirm: '' });
        setIsSubmittingAction(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col font-sans max-w-md mx-auto relative overflow-hidden text-secondary shadow-2xl border-x border-slate-200 pb-24">
      {activeToast && (
        <ToastNotification title={activeToast.title} message={activeToast.message} type={activeToast.type} onClose={() => setActiveToast(null)} />
      )}

      {/* CENTRAL DE ALERTAS */}
      {isNotifOpen && (
        <div className="absolute inset-0 bg-[#222e3d] z-[2000] flex flex-col animate-in slide-in-from-right duration-300 overflow-hidden">
           <div className="w-full pt-12 pb-6 flex items-center justify-between px-6 shrink-0">
              <div className="flex items-center gap-3">
                 <div className="w-10 h-10 bg-primary text-white rounded-xl flex items-center justify-center shadow-lg">
                    <Bell size={20} strokeWidth={3} />
                 </div>
                 <div className="flex flex-col">
                    <h2 className="text-lg font-black text-white uppercase italic tracking-tighter leading-none">Alertas</h2>
                    <p className="text-[8px] font-black text-primary uppercase tracking-[0.4em] mt-1">{unreadCount} Mensagens</p>
                 </div>
              </div>
              <div className="flex items-center gap-3">
                 <button onClick={() => onMarkAllRead()} className="text-[9px] font-black text-primary uppercase tracking-widest hover:brightness-125">Limpar</button>
                 <button onClick={() => setIsNotifOpen(false)} className="p-2 bg-white/5 text-white rounded-lg hover:bg-white/10 active:scale-90 transition-all"><X size={20} strokeWidth={3} /></button>
              </div>
           </div>
           
           <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3 scrollbar-hide">
              {notifications.length > 0 ? notifications.map((n: any) => (
                <div key={n.id} className={`w-full bg-white rounded-[24px] p-4 shadow-lg flex items-center gap-4 relative animate-in fade-in transition-all ${!n.read ? 'border-l-4 border-l-primary' : 'opacity-60'}`}>
                   <div className={`w-10 h-10 rounded-full shrink-0 flex items-center justify-center ${n.type === 'success' ? 'bg-green-50 text-green-500' : n.type === 'warning' ? 'bg-amber-50 text-amber-500' : 'bg-blue-50 text-blue-500'}`}>
                      {n.type === 'warning' ? <AlertTriangle size={18} strokeWidth={2.5} /> : <Info size={18} strokeWidth={2.5} />}
                   </div>
                   <div className="flex-1 min-w-0 pr-10">
                      <h4 className="text-[10px] font-black uppercase text-[#1e293b] leading-tight truncate">{n.title}</h4>
                      <p className="text-[9px] font-medium text-slate-500 mt-0.5 leading-relaxed line-clamp-2">{n.message}</p>
                   </div>
                   <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[8px] font-bold text-slate-300 uppercase italic">{n.timestamp}</div>
                </div>
              )) : (
                <div className="h-full flex flex-col items-center justify-center opacity-10 text-white gap-4">
                   <Bell size={64} strokeWidth={1} />
                   <p className="text-xs font-black uppercase tracking-[0.5em]">Sem alertas</p>
                </div>
              )}
           </div>
           
           <div className="p-6 bg-[#222e3d] border-t border-white/5 shrink-0">
              <button onClick={() => setIsNotifOpen(false)} className="w-full py-4 bg-white/5 text-white/40 rounded-2xl font-black uppercase text-[9px] tracking-[0.3em]">Voltar ao Início</button>
           </div>
        </div>
      )}

      {/* HEADER PRINCIPAL */}
      <header className="px-6 pt-12 pb-6 shrink-0 z-[60] bg-[#1e293b] text-white relative overflow-hidden rounded-b-[40px] shadow-lg">
        <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12 scale-150 pointer-events-none"><Truck size={120} /></div>
        <div className="flex justify-between items-center relative z-10">
           <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center text-white font-black text-sm italic shadow-xl border-2 border-white/20 overflow-hidden">
                {user.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : user.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-sm font-black tracking-tight text-white uppercase leading-none italic">{user.name.split(' ')[0]}</h2>
                <div className="flex items-center gap-1.5 mt-2">
                   <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                   <span className="text-[7px] font-black text-white/50 uppercase tracking-[0.2em]">Unidade: {myCompany.name}</span>
                </div>
              </div>
           </div>
           <div className="flex items-center gap-2">
              <button onClick={() => setIsNotifOpen(true)} className="p-3 bg-white/5 rounded-xl text-white/40 hover:text-white transition-all active:scale-95 relative">
                 <Bell size={20} />
                 {unreadCount > 0 && <span className="absolute top-2.5 right-2.5 w-3 h-3 bg-red-500 rounded-full border-2 border-[#1e293b]"></span>}
              </button>
              <button onClick={onLogout} className="p-3 bg-white/5 rounded-xl text-white/40 hover:text-white transition-colors"><LogOut size={20} /></button>
           </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-5 space-y-4 scrollbar-hide pt-4 pb-10">
        {activeTab === 'route' && (
           <div className="space-y-4 animate-in fade-in">
              <div className="flex justify-between items-center px-1">
                 <h3 className="text-[9px] font-black tracking-[0.4em] text-slate-400 uppercase italic">Minhas Rotas</h3>
                 <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]"></div>
                    <span className="text-[8px] font-black text-slate-300 uppercase">GPS Live</span>
                 </div>
              </div>
              {orders.filter((o:any) => o.teamId === user.teamId && o.status !== 'CONCLUIDO').map((o:any) => (
                <DriverOrderCard key={o.id} order={o} onSelect={() => setSelectedOrder(o)} />
              ))}
           </div>
        )}

        {activeTab === 'finance' && (
           <div className="space-y-6 animate-in slide-in-from-bottom-4">
              <div className="bg-[#0f172a] py-6 px-6 rounded-[32px] text-white shadow-xl relative overflow-hidden border-b-4 border-primary text-center">
                 <div className="absolute top-0 right-0 p-4 opacity-5 rotate-12"><DollarSign size={60} /></div>
                 <p className="text-[8px] font-black text-primary uppercase tracking-[0.3em] mb-1">Ganhos Acumulados</p>
                 <p className="text-3xl font-black tracking-tighter italic">R$ {totalBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              </div>

              {/* BOTÕES FINANCEIROS - RESTAURADOS CONFORME PRINT 2 */}
              <div className="grid grid-cols-2 gap-4">
                 <button 
                  onClick={() => { setRequestType('REFUND'); setShowRequestModal(true); }} 
                  className="bg-white p-8 rounded-[36px] border border-slate-100 shadow-sm flex flex-col items-center gap-4 active:scale-95 transition-all group"
                 >
                    <div className="p-4 bg-red-50 text-red-500 rounded-3xl group-hover:scale-110 transition-transform">
                       <Receipt size={32} />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Reembolso</span>
                 </button>
                 <button 
                  onClick={() => { setRequestType('EXTRA'); setShowRequestModal(true); }} 
                  className="bg-white p-8 rounded-[36px] border border-slate-100 shadow-sm flex flex-col items-center gap-4 active:scale-95 transition-all group"
                 >
                    <div className="p-4 bg-emerald-50 text-emerald-600 rounded-3xl group-hover:scale-110 transition-transform">
                       <PackagePlus size={32} />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Item Extra</span>
                 </button>
              </div>

              <div className="space-y-2">
                 <h3 className="text-[9px] font-black tracking-[0.4em] text-slate-300 uppercase italic px-1">Extrato de Diárias</h3>
                 {filteredTransactions.map((tx: any) => (
                    <div key={tx.id} className="bg-white p-3.5 rounded-2xl border border-slate-50 shadow-sm flex items-center justify-between gap-2">
                       <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-emerald-50 text-emerald-500 shadow-inner">
                             <CheckCircle2 size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                             <p className="text-[10px] font-black text-secondary uppercase truncate leading-none mb-1">{tx.description || 'Diária Operacional'}</p>
                             <p className="text-[7px] font-bold text-slate-300 uppercase tracking-widest italic">{new Date(tx.date).toLocaleDateString('pt-BR')}</p>
                          </div>
                       </div>
                       <p className="text-[11px] font-black text-emerald-600 italic">+ R$ {tx.amount.toLocaleString('pt-BR')}</p>
                    </div>
                 ))}
              </div>
           </div>
        )}

        {activeTab === 'profile' && (
           <div className="space-y-6 animate-in fade-in pb-10 pt-2">
              <div className="flex flex-col items-center text-center">
                 <div onClick={() => profilePhotoRef.current?.click()} className="w-24 h-24 rounded-[36px] bg-white border-4 border-white shadow-xl flex items-center justify-center text-3xl font-black text-primary overflow-hidden italic relative cursor-pointer group">
                    {user.avatar ? <img src={user.avatar} className="w-full h-full object-cover" /> : user.name.charAt(0)}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                       <Camera size={24} className="text-white" />
                    </div>
                    <input type="file" ref={profilePhotoRef} className="hidden" accept="image/*" onChange={handleUpdateProfilePhoto} />
                 </div>
                 <h3 className="text-lg font-black text-secondary uppercase italic mt-4 leading-none">{user.name}</h3>
                 <p className="text-[8px] font-black text-slate-400 uppercase tracking-[0.5em] mt-1.5">Matrícula: {user.id.slice(-6)}</p>
                 <Badge className="bg-primary/5 text-primary border-none font-black text-[7px] px-4 py-1.5 rounded-full mt-4 uppercase tracking-[0.2em] italic">
                    <Building2 size={10} className="inline mr-1" /> Unidade Sorocaba
                 </Badge>
              </div>

              {/* BLOCO DOCUMENTO DO MOTORISTA (CNH) */}
              <Card className="bg-white border border-slate-100 p-5 rounded-[32px] shadow-sm space-y-4">
                 <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                       <div className="p-2.5 bg-primary/10 text-primary rounded-xl"><FileBadge size={18} /></div>
                       <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Documento (CNH)</span>
                    </div>
                    {user.cnhPhoto ? (
                       <Badge className="bg-green-50 text-green-600 border-none text-[8px] font-black uppercase">Enviado</Badge>
                    ) : (
                       <Badge className="bg-red-50 text-red-600 border-none text-[8px] font-black uppercase">Pendente</Badge>
                    )}
                 </div>
                 <button 
                  onClick={() => cnhPhotoRef.current?.click()}
                  className="w-full py-4 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-2 group active:scale-95 transition-all hover:border-primary/40 hover:bg-primary/5"
                 >
                    {user.cnhPhoto ? (
                      <div className="flex items-center gap-2 text-primary font-black text-[9px] uppercase"><RefreshCw size={14} /> Atualizar Documento</div>
                    ) : (
                      <>
                        <UploadCloud size={24} className="text-slate-300 group-hover:text-primary transition-colors" />
                        <span className="text-[9px] font-black text-slate-400 uppercase group-hover:text-primary">Subir Foto da CNH</span>
                      </>
                    )}
                    <input type="file" ref={cnhPhotoRef} className="hidden" accept="image/*" capture="environment" onChange={handleUpdateCnh} />
                 </button>
              </Card>

              {/* BLOCO SEGURANÇA E ACESSO */}
              <div className="space-y-3">
                 <button 
                  onClick={() => setShowResetPasswordModal(true)}
                  className="w-full py-5 bg-white border border-slate-100 rounded-[28px] text-secondary flex items-center justify-between px-8 shadow-sm active:scale-95 transition-all group"
                 >
                    <div className="flex items-center gap-4">
                       <div className="p-2.5 bg-slate-50 text-slate-400 rounded-2xl group-hover:bg-primary group-hover:text-white transition-colors"><KeyRound size={20} /></div>
                       <span className="text-[10px] font-black uppercase tracking-widest">Redefinir Senha</span>
                    </div>
                    <ChevronRight size={18} className="text-slate-300" />
                 </button>

                 <button onClick={() => setShowSosModal(true)} className="w-full bg-red-600 p-6 rounded-[32px] text-white flex items-center justify-between shadow-xl active:scale-95 transition-all">
                    <div className="flex items-center gap-4">
                       <div className="bg-white/20 p-3 rounded-2xl animate-pulse"><Siren size={28} /></div>
                       <div className="text-left">
                          <p className="text-lg font-black uppercase italic leading-none">ALERTA SOS</p>
                          <p className="text-[8px] font-bold text-white/60 uppercase tracking-widest mt-1">Pânico ou Emergência</p>
                       </div>
                    </div>
                    <ChevronRight size={24} className="opacity-40" />
                 </button>
              </div>

              <button onClick={onLogout} className="w-full py-4 text-red-500 text-[8px] font-black uppercase tracking-[0.4em] border border-red-50 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-all mt-4">Sair do App</button>
           </div>
        )}
      </main>

      {/* MODAL: SOLICITAÇÃO FINANCEIRA (REEMBOLSO/EXTRA) */}
      {showRequestModal && (
         <div className="fixed inset-0 bg-secondary/95 backdrop-blur-md z-[5000] flex items-center justify-center p-6 animate-in fade-in">
            <div className="bg-white rounded-[50px] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95">
               <div className={`p-8 ${requestType === 'REFUND' ? 'bg-red-600' : 'bg-emerald-600'} text-white flex justify-between items-center`}>
                  <div className="flex items-center gap-4">
                     <div className="p-3 bg-white/20 rounded-2xl shadow-inner">
                        {requestType === 'REFUND' ? <Receipt size={24} /> : <PackagePlus size={24} />}
                     </div>
                     <div>
                        <h2 className="text-xl font-black uppercase italic leading-none">{requestType === 'REFUND' ? 'Solicitar Reembolso' : 'Lançar Item Extra'}</h2>
                        <p className="text-[8px] font-bold text-white/60 uppercase tracking-widest mt-2">Auditoria Financeira</p>
                     </div>
                  </div>
                  <button onClick={() => { setShowRequestModal(false); resetRequestForm(); }} className="p-2 hover:bg-white/10 rounded-xl transition-all"><X size={24} /></button>
               </div>
               
               <div className="p-8 space-y-6">
                  <div className="space-y-1.5">
                     <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Vincular à Ordem de Serviço</label>
                     <select 
                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary text-sm appearance-none cursor-pointer"
                        value={reqOrderId}
                        onChange={e => setReqOrderId(e.target.value)}
                     >
                        <option value="">Selecione a O.S...</option>
                        {orders.filter((o:any) => o.teamId === user.teamId).map((o:any) => (
                           <option key={o.id} value={o.id}>{o.nomeEvento} (OS {o.id.slice(-4)})</option>
                        ))}
                     </select>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Valor Bruto (R$)</label>
                        <input 
                           type="number" 
                           className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-black text-secondary text-2xl shadow-inner"
                           placeholder="0,00"
                           value={reqAmount}
                           onChange={e => setReqAmount(e.target.value)}
                        />
                     </div>
                     <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">O que foi gasto?</label>
                        <input 
                           className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary text-sm shadow-inner"
                           placeholder="Ex: Diesel Caminhão, 10 cadeiras..."
                           value={reqDesc}
                           onChange={e => setReqDesc(e.target.value)}
                        />
                     </div>
                  </div>

                  <div className="space-y-3">
                     <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Evidência / Recibo</label>
                     <button 
                        onClick={() => reqPhotoRef.current?.click()}
                        className="w-full py-5 border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center gap-2 group hover:border-primary/40 hover:bg-primary/5 transition-all overflow-hidden"
                     >
                        {reqPhoto ? (
                           <img src={reqPhoto} className="w-full h-24 object-cover rounded-xl" />
                        ) : (
                           <>
                              <Camera size={24} className="text-slate-300 group-hover:text-primary transition-all" />
                              <span className="text-[9px] font-black text-slate-400 uppercase">Tirar Foto do Recibo</span>
                           </>
                        )}
                        <input type="file" ref={reqPhotoRef} className="hidden" accept="image/*" capture="environment" onChange={handleCaptureRequestPhoto} />
                     </button>
                  </div>

                  <button 
                     disabled={isSubmittingAction}
                     onClick={handleSendFinancialRequest}
                     className={`w-full py-6 ${requestType === 'REFUND' ? 'bg-red-600 shadow-red-100' : 'bg-emerald-600 shadow-emerald-100'} text-white rounded-[32px] font-black uppercase text-xs tracking-widest shadow-xl flex items-center justify-center gap-3 transition-all active:scale-95`}
                  >
                     {isSubmittingAction ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                     Enviar para Central
                  </button>
               </div>
            </div>
         </div>
      )}

      {/* MODAL: ALERTA SOS - RESTAURADO COM OPÇÃO OUTROS */}
      {showSosModal && (
        <div className="fixed inset-0 bg-secondary/95 backdrop-blur-md z-[5000] flex items-center justify-center p-6 animate-in fade-in">
           <div className="bg-white rounded-[50px] w-full max-w-sm shadow-[0_50px_100px_rgba(0,0,0,0.5)] overflow-hidden animate-in zoom-in-95">
              <div className="p-10 bg-red-600 text-white flex flex-col items-center gap-4 text-center shrink-0">
                 <div className="p-5 bg-white/20 rounded-[32px] animate-pulse">
                    <Siren size={48} />
                 </div>
                 <h2 className="text-2xl font-black uppercase italic">ALERTA DE EMERGÊNCIA</h2>
                 <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest">Seu GPS e status serão enviados à central agora.</p>
              </div>
              <div className="p-8 space-y-3 overflow-y-auto max-h-[60vh] scrollbar-hide">
                 <SosOption active={sosType === 'ACIDENTE'} label="Acidente de Trânsito" icon={Siren} onClick={() => setSosType('ACIDENTE')} />
                 <SosOption active={sosType === 'QUEBRA'} label="Quebra Mecânica" icon={Wrench} onClick={() => setSosType('QUEBRA')} />
                 <SosOption active={sosType === 'SEGURANCA'} label="Segurança / Assalto" icon={ShieldAlert} onClick={() => setSosType('SEGURANCA')} />
                 <SosOption active={sosType === 'OUTROS'} label="Outros Problemas" icon={MessageSquare} onClick={() => setSosType('OUTROS')} />
                 
                 {sosType === 'OUTROS' && (
                    <div className="space-y-2 mt-4 animate-in slide-in-from-top-2 duration-300">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-4">Descreva a Emergência</label>
                       <textarea 
                          autoFocus
                          className="w-full bg-slate-50 border border-slate-100 p-5 rounded-3xl outline-none font-bold text-secondary text-xs focus:bg-white focus:ring-4 focus:ring-red-500/5 transition-all resize-none shadow-inner"
                          placeholder="Digite aqui o que está acontecendo..."
                          value={sosDescription}
                          onChange={e => setSosDescription(e.target.value)}
                          rows={3}
                       />
                    </div>
                 )}

                 <div className="pt-6 flex flex-col gap-3">
                    <button 
                        disabled={!sosType || isSubmittingAction}
                        onClick={handleExecuteSos}
                        className="w-full py-6 bg-red-600 text-white rounded-[36px] font-black uppercase text-xs tracking-widest shadow-xl disabled:opacity-30 flex items-center justify-center gap-3 active:scale-95 transition-all"
                    >
                        {isSubmittingAction ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                        Disparar Alerta SOS
                    </button>
                    <button onClick={() => { setShowSosModal(false); setSosType(null); setSosDescription(''); }} className="w-full py-2 text-[9px] font-black text-slate-300 uppercase tracking-widest hover:text-secondary transition-colors">Cancelar</button>
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* MODAL: REDEFINIR SENHA */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-secondary/95 backdrop-blur-md z-[5000] flex items-center justify-center p-6 animate-in fade-in">
           <div className="bg-white rounded-[50px] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95">
              <div className="p-10 bg-primary text-white flex flex-col items-center gap-4 text-center">
                 <div className="p-4 bg-white/20 rounded-2xl">
                    <Lock size={32} />
                 </div>
                 <h2 className="text-xl font-black uppercase italic leading-none">Alterar Senha</h2>
                 <p className="text-[8px] font-bold text-white/60 uppercase tracking-widest">Segurança da Unidade</p>
              </div>
              <div className="p-8 space-y-6">
                 <div className="space-y-4">
                    <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Nova Senha</label>
                       <input 
                        type="password"
                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all shadow-inner"
                        placeholder="••••••••"
                        value={passwordData.new}
                        onChange={e => setPasswordData({...passwordData, new: e.target.value})}
                       />
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Confirmar Senha</label>
                       <input 
                        type="password"
                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-secondary focus:bg-white focus:ring-4 focus:ring-primary/5 transition-all shadow-inner"
                        placeholder="••••••••"
                        value={passwordData.confirm}
                        onChange={e => setPasswordData({...passwordData, confirm: e.target.value})}
                       />
                    </div>
                 </div>
                 <button 
                    disabled={isSubmittingAction}
                    onClick={handleResetPassword}
                    className="w-full py-5 bg-primary text-white rounded-[24px] font-black uppercase text-xs tracking-widest shadow-xl flex items-center justify-center gap-2 active:scale-95 transition-all"
                 >
                    {isSubmittingAction ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} strokeWidth={4} />}
                    Atualizar Senha
                 </button>
                 <button onClick={() => setShowResetPasswordModal(false)} className="w-full py-2 text-[9px] font-black text-slate-300 uppercase tracking-widest">Fechar</button>
              </div>
           </div>
        </div>
      )}

      {/* BOTTOM NAV */}
      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md h-16 bg-white/90 backdrop-blur-2xl border-t border-slate-100 flex items-center justify-around px-4 z-[100] shadow-2xl">
         <TabBtn active={activeTab === 'route'} icon={Truck} label="ROTA" onClick={() => setActiveTab('route')} />
         <TabBtn active={activeTab === 'finance'} icon={DollarSign} label="GANHOS" onClick={() => setActiveTab('finance')} />
         <TabBtn active={activeTab === 'profile'} icon={User} label="PERFIL" onClick={() => setActiveTab('profile')} />
      </nav>

      {selectedOrder && (
        <DriverOrderFlow order={selectedOrder} onClose={() => setSelectedOrder(null)} onComplete={onUpdateOrder} />
      )}
    </div>
  );
};

const SosOption = ({ label, icon: Icon, active, onClick }: any) => (
  <button onClick={onClick} className={`w-full p-5 rounded-[28px] border-2 transition-all flex items-center justify-between ${active ? 'bg-red-50 border-red-500 text-red-600 shadow-lg' : 'bg-white border-slate-100 text-slate-400'}`}>
     <div className="flex items-center gap-4">
        <Icon size={20} />
        <span className="text-[11px] font-black uppercase tracking-tight">{label}</span>
     </div>
     <div className={`w-6 h-6 rounded-full border-2 ${active ? 'bg-red-500 border-red-500' : 'border-slate-100'}`}>
        {active && <Check size={14} className="text-white mx-auto mt-0.5" strokeWidth={4} />}
     </div>
  </button>
);

const TabBtn = ({ active, icon: Icon, label, onClick }: any) => (
  <button onClick={onClick} className={`flex flex-col items-center justify-center gap-1 transition-all flex-1 h-full relative ${active ? 'text-primary' : 'text-slate-300'}`}>
     <Icon size={18} strokeWidth={active ? 2.5 : 1.5} />
     <span className={`text-[7px] font-black uppercase tracking-widest transition-opacity ${active ? 'opacity-100' : 'opacity-40'}`}>{label}</span>
     {active && <div className="absolute top-0 w-6 h-1 bg-primary rounded-b-full"></div>}
  </button>
);

const DriverOrderCard = ({ order, onSelect }: any) => (
  <div onClick={onSelect} className="bg-white p-5 rounded-[32px] shadow-sm border border-slate-100 group active:scale-[0.98] transition-all flex flex-col gap-4">
    <div className="flex justify-between items-start">
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
           <Badge className="bg-primary/10 text-primary border-none text-[7px] px-2 py-0.5 font-black uppercase">OS: {order.id.slice(-6)}</Badge>
           <div className={`w-1.5 h-1.5 rounded-full ${order.status === 'MONTAGEM' ? 'bg-purple-500 animate-pulse' : 'bg-blue-400'}`}></div>
        </div>
        <h4 className="text-sm font-black text-secondary uppercase leading-tight truncate italic group-hover:text-primary transition-colors">{order.nomeEvento}</h4>
        <p className="text-[9px] text-slate-400 font-bold uppercase flex items-center gap-1 mt-1.5"><MapPin size={10} strokeWidth={3} className="text-primary" /> {order.horarioEntrega} • Abrir Roteiro</p>
      </div>
      <div className="p-3 bg-gray-50 rounded-2xl text-slate-300 group-hover:bg-primary group-hover:text-white transition-all shadow-inner"><ChevronRight size={20} strokeWidth={3} /></div>
    </div>
  </div>
);

const DriverOrderFlow = ({ order, onClose, onComplete }: any) => {
  const [step, setStep] = useState<'CHECKLIST' | 'PHOTO' | 'SIGNATURE' | 'DONE'>('CHECKLIST');
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [deliveryPhoto, setDeliveryPhoto] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const allChecked = useMemo(() => order.materials.every((m: any) => checkedItems[m.id]), [order.materials, checkedItems]);
  const toggleItem = (id: string) => setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));

  const handleCapturePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setDeliveryPhoto(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const startDrawing = (e: any) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.moveTo(x, y);
    const draw = (moveEvent: any) => {
      const mx = (moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX) - rect.left;
      const my = (moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY) - rect.top;
      ctx.lineTo(mx, my);
      ctx.stroke();
    };
    const stopDrawing = () => {
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('touchmove', draw);
      setSignatureData(canvas.toDataURL());
    };
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('touchmove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('touchend', stopDrawing);
  };

  const handleFinalFinish = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      onComplete({ ...order, status: OrderStatus.CONCLUIDO, signature: signatureData, eventPhoto: deliveryPhoto });
      setStep('DONE');
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="absolute inset-0 bg-white z-[500] flex flex-col animate-in slide-in-from-bottom duration-300">
      <div className="px-6 pt-12 pb-4 flex justify-between items-center shrink-0 border-b border-slate-100 bg-[#f8fafc]">
         <button onClick={onClose} className="p-2 bg-white rounded-xl text-secondary shadow-sm active:scale-90 transition-all"><X size={20} strokeWidth={2.5} /></button>
         <div className="flex flex-col items-center">
            <h3 className="text-[8px] font-black uppercase tracking-[0.4em] text-slate-400 italic">Protocolo Operacional</h3>
            <div className="flex gap-1 mt-1">
               <div className={`w-1.5 h-1.5 rounded-full ${step === 'CHECKLIST' ? 'bg-primary' : 'bg-slate-200'}`}></div>
               <div className={`w-1.5 h-1.5 rounded-full ${step === 'PHOTO' ? 'bg-primary' : 'bg-slate-200'}`}></div>
               <div className={`w-1.5 h-1.5 rounded-full ${step === 'SIGNATURE' ? 'bg-primary' : 'bg-slate-200'}`}></div>
            </div>
         </div>
         <div className="w-10"></div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
         {step === 'CHECKLIST' && (
           <div className="space-y-6 animate-in fade-in">
              <div><h2 className="text-2xl font-black text-secondary uppercase italic leading-tight">Conferência de Carga</h2><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Valide os materiais no ato do descarregamento</p></div>
              <div className="space-y-3">
                 {order.materials.map((m: any) => (
                   <button key={m.id} onClick={() => toggleItem(m.id)} className={`w-full p-5 rounded-3xl border-2 transition-all flex items-center justify-between ${checkedItems[m.id] ? 'bg-emerald-50 border-emerald-500 shadow-md scale-[1.02]' : 'bg-white border-slate-100'}`}>
                      <div className="flex items-center gap-4 text-left">
                         <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs italic ${checkedItems[m.id] ? 'bg-emerald-500 text-white' : 'bg-slate-50 text-slate-400'}`}>{m.qty}</div>
                         <div><p className={`text-xs font-black uppercase leading-none ${checkedItems[m.id] ? 'text-emerald-700' : 'text-secondary'}`}>{m.name}</p><p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">Confirmação Local</p></div>
                      </div>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${checkedItems[m.id] ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-100'}`}>{checkedItems[m.id] && <Check size={14} strokeWidth={4} />}</div>
                   </button>
                 ))}
              </div>
              <button disabled={!allChecked} onClick={() => setStep('PHOTO')} className="w-full py-6 bg-primary text-white rounded-[32px] font-black uppercase text-xs tracking-widest shadow-xl shadow-primary/20 disabled:opacity-30 transition-all flex items-center justify-center gap-3">Próximo: Registro Visual <ChevronRight size={18} strokeWidth={3} /></button>
           </div>
         )}
         {step === 'PHOTO' && (
            <div className="space-y-8 animate-in slide-in-from-right-4 duration-300">
               <div><h2 className="text-2xl font-black text-secondary uppercase italic leading-none">Foto da Montagem</h2><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">Evidência fotográfica obrigatória para encerramento</p></div>
               <div onClick={() => photoInputRef.current?.click()} className="aspect-square bg-slate-50 border-4 border-dashed border-slate-200 rounded-[40px] flex flex-col items-center justify-center gap-4 group cursor-pointer relative overflow-hidden transition-all hover:bg-slate-100">
                  {deliveryPhoto ? <img src={deliveryPhoto} className="w-full h-full object-cover animate-in zoom-in" /> : <><div className="p-6 bg-white rounded-[32px] shadow-xl text-primary"><Camera size={48} /></div><p className="text-[10px] font-black uppercase text-slate-400 tracking-widest italic">Tirar Foto do Material</p></>}
                  <input type="file" ref={photoInputRef} accept="image/*" capture="environment" className="hidden" onChange={handleCapturePhoto} />
               </div>
               <div className="flex gap-3">
                  <button onClick={() => setStep('CHECKLIST')} className="p-6 bg-slate-100 text-slate-400 rounded-[32px]"><ArrowLeft size={24} /></button>
                  <button disabled={!deliveryPhoto} onClick={() => setStep('SIGNATURE')} className="flex-1 py-6 bg-primary text-white rounded-[32px] font-black uppercase text-xs tracking-widest shadow-xl active:scale-95 transition-all flex items-center justify-center gap-3">Prosseguir <ChevronRight size={18} strokeWidth={3} /></button>
               </div>
            </div>
         )}
         {step === 'SIGNATURE' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
               <div><h2 className="text-2xl font-black text-secondary uppercase italic leading-none">Recebimento do Cliente</h2><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 italic">Apresente o romaneio e colha a assinatura</p></div>
               <div className="bg-white p-6 rounded-3xl border-2 border-slate-100 space-y-5 shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-[0.03] rotate-12 scale-150"><FileBadge size={100} /></div>
                  <div className="flex justify-between items-start border-b border-slate-100 pb-4"><div><p className="text-[8px] font-black text-primary uppercase tracking-[0.4em]">Packing List Interno</p><p className="text-xs font-black text-secondary uppercase italic">Romaneio de Entrega: #{order.id.slice(-6)}</p></div><ShieldCheck size={20} className="text-emerald-500" /></div>
                  <div className="space-y-3"><p className="text-[10px] text-slate-600 font-bold leading-relaxed">Declaro que recebi os equipamentos em perfeitas condições para o evento <span className="text-secondary font-black underline">{order.nomeEvento}</span>. Responsabilizo-me pela guarda até a retirada agendada.</p>
                     <div className="bg-slate-50 p-4 rounded-2xl space-y-1.5">{order.materials.map((m: any) => (<div key={m.id} className="flex justify-between text-[9px] font-black uppercase text-slate-400"><span>• {m.name}</span><span>{m.qty} UN</span></div>))}</div>
                  </div>
               </div>
               <div className="space-y-2">
                  <div className="bg-white border-4 border-slate-100 rounded-[32px] overflow-hidden relative shadow-lg touch-none"><canvas ref={canvasRef} width={350} height={180} className="w-full h-auto cursor-crosshair" onMouseDown={startDrawing} onTouchStart={startDrawing} /><button onClick={() => { const canvas = canvasRef.current; if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height); setSignatureData(null); }} className="absolute top-4 right-4 p-2 bg-slate-50 text-slate-300 rounded-lg"><RefreshCw size={14} /></button></div>
                  <p className="text-center text-[8px] font-black text-slate-300 uppercase tracking-widest italic">Assine digitalmente no campo acima</p>
               </div>
               <div className="flex gap-3">
                  <button onClick={() => setStep('PHOTO')} className="p-6 bg-slate-100 text-slate-400 rounded-[32px]"><ArrowLeft size={24} /></button>
                  <button disabled={!signatureData || isSubmitting} onClick={handleFinalFinish} className="flex-1 py-6 bg-secondary text-white rounded-[32px] font-black uppercase text-xs tracking-widest shadow-2xl active:scale-95 transition-all flex items-center justify-center gap-3">{isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} strokeWidth={3} />}{isSubmitting ? 'Auditando...' : 'Finalizar Entrega'}</button>
               </div>
            </div>
         )}
         {step === 'DONE' && (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-8 animate-in zoom-in">
               <div className="w-28 h-28 bg-emerald-50 text-emerald-500 rounded-[44px] flex items-center justify-center shadow-inner relative"><CheckCircle2 size={56} strokeWidth={3} /><div className="absolute inset-0 rounded-[44px] border-4 border-emerald-500 animate-ping opacity-20"></div></div>
               <div className="space-y-2"><h2 className="text-3xl font-black text-secondary uppercase italic">Evento Entregue!</h2><p className="text-sm font-medium text-slate-400 leading-relaxed px-10">Os dados, fotos e romaneio assinado foram enviados à central em Sorocaba.</p></div>
               <button onClick={onClose} className="w-full py-6 bg-primary text-white rounded-[32px] font-black uppercase text-xs tracking-widest shadow-xl shadow-primary/20 active:scale-95 transition-all">Voltar para Minhas Rotas</button>
            </div>
         )}
      </div>
    </div>
  );
};
